// AS ETIQUETAS MARCADAS NO BALCAO — a regra de quem pode desmarcar o que ([DA-722]).
//
// Regra dele, em 12/09/2026, sobre o bloco "Cadastrar o WhatsApp agora" do Enviar ao Caixa:
//
//   1. Contato novo: marca quantas etiquetas for necessario.
//   2. Pesquisou o telefone e encontrou: exibe as que ja estavam gravadas e deixa marcar novas.
//   3. Nao deixa DESMARCAR as que ja foram gravadas — so' o admin consegue.
//      *"Ao nao permitir um usuario desmarcar uma Etiqueta eu evito que ele desmarque por engano.
//      Se a mudanca for legitima, o Admin realiza a tarefa"*.
//
// ⚠️ **A emenda de 12/09, aceita por ele: travado e' o que veio do BANCO, nunca o gesto em curso.**
// O chip que a pessoa marcou ha tres segundos continua destravavel — travar tambem esse faria um
// clique errado de um segundo virar tarefa pro admin, que e' o oposto do que a regra 3 protege.
//
// ⚠️ Isto mora fora do componente porque e' a regra, nao a tela: o `useState` la' dentro nao se
// testa, e o que decide quem pode apagar carimbo de contato merece guarda propria.

/**
 * A etiqueta que ABRE a lista do balcao (pedido dele em 12/09/2026: *"tem como colocar a etiqueta
 * CONSUMIDOR para ser a primeira na lista?"*).
 *
 * ⚠️ **Casa pelo NOME, e um rename desfaz o topo** — de proposito: id cravado sobrevive ao rename
 * e morre na exclusao, calado, e ninguem liga um numero a uma etiqueta olhando o codigo. Se ela
 * sumir da lista, o pior que acontece e' a ordem voltar a ser toda alfabetica.
 * ⚠️ Comparacao em MAIUSCULO porque a base inteira e' maiuscula desde a migration 0288
 * ([DA-716]) — casar por nome em minusculo foi exatamente o defeito daquele dia.
 */
export const ETIQUETA_QUE_ABRE_A_LISTA = 'CONSUMIDOR';

/**
 * A ordem da lista do balcao: a `CONSUMIDOR` primeiro, o resto em ordem alfabetica.
 *
 * ⛔ **Uma copia so'.** Os DOIS caminhos do Enviar ao Caixa mostram esta lista — a coluna do meio
 * (contato novo) e o combo do cliente ja escolhido —, e ate 12/09/2026 cada um tinha o proprio
 * `sort` escrito a mao: o buraco da [DA-659], onde a copia esquecida e' sempre a que ninguem abre.
 *
 * ⚠️ **Ordena pelo ROTULO que a tela IMPRIME** (`apelido || nome`), nunca pelo `nome`: ordenar por
 * um campo que nao esta na tela da' uma ordem que ninguem explica olhando ([DA-701] p.5).
 * `localeCompare` pt-BR porque `SEGURANÇA` tem que cair entre SEGURANCA e SERRALHERIA.
 */
export function ordenarParaOBalcao<T extends { nome: string; apelido: string | null }>(
  etiquetas: T[],
): T[] {
  const rotulo = (e: T) => e.apelido || e.nome;
  const topo = (e: T) => (e.nome.toUpperCase() === ETIQUETA_QUE_ABRE_A_LISTA ? 0 : 1);
  return [...etiquetas].sort(
    (a, b) => topo(a) - topo(b) || rotulo(a).localeCompare(rotulo(b), 'pt-BR', { sensitivity: 'base' }),
  );
}

export interface EstadoEtiquetas {
  /** Ids que o contato JA tinha quando o telefone foi identificado. */
  gravadas: number[];
  /** Ids marcados AGORA, nesta tela, ainda nao gravados. */
  novas: number[];
  /** Ids gravados que o admin pediu pra retirar (vazio pra todo mundo que nao e' admin). */
  remover: number[];
}

export const ESTADO_VAZIO: EstadoEtiquetas = { gravadas: [], novas: [], remover: [] };

/**
 * O que o seletor deve desenhar como MARCADO: o que ja estava (menos o que o admin acabou de
 * tirar) mais o que foi marcado agora.
 */
export function marcadasNaTela(e: EstadoEtiquetas): number[] {
  return [...e.gravadas.filter((id) => !e.remover.includes(id)), ...e.novas];
}

/**
 * O que o seletor deve desenhar como TRAVADO.
 *
 * ⛔ Para o admin e' sempre vazio — ele e' justamente quem a regra 3 autoriza. Para o resto, todo
 * id que veio do banco, inclusive os que ele nao ve no seletor (etiqueta oculta nao e' desenhada,
 * entao sobra sem efeito — e e' de proposito: a lista nao depende do que a tela mostra).
 */
export function travadasNaTela(e: EstadoEtiquetas, ehAdmin: boolean): number[] {
  return ehAdmin ? [] : e.gravadas;
}

/**
 * O clique num chip. Devolve o estado novo — ou o MESMO objeto quando o gesto e' inerte (chip
 * travado), pra tela nao se redesenhar a toa.
 */
export function alternar(e: EstadoEtiquetas, id: number, ehAdmin: boolean): EstadoEtiquetas {
  // Marcada agora: desmarcar e' sempre permitido, pra qualquer perfil.
  if (e.novas.includes(id)) return { ...e, novas: e.novas.filter((x) => x !== id) };

  if (e.gravadas.includes(id)) {
    if (!ehAdmin) return e; // regra 3: quem nao e' admin nao retira o que ja esta gravado.
    // Admin: alterna entre "vai sair" e "fica".
    return e.remover.includes(id)
      ? { ...e, remover: e.remover.filter((x) => x !== id) }
      : { ...e, remover: [...e.remover, id] };
  }

  return { ...e, novas: [...e.novas, id] };
}

/**
 * O que vai no corpo do `capturar-contato`.
 *
 * ⛔ **Manda so' o DELTA, nunca o conjunto inteiro.** O servidor grava de forma aditiva e apaga
 * por id; mandar "o conjunto que deve existir" reintroduziria o apaga-tudo-e-regrava do
 * `PUT /contatos/:id/etiquetas`, que e' o que apagaria o carimbo do rodizio ([DA-687]).
 */
export function payloadDoCapturar(e: EstadoEtiquetas): {
  etiqueta_ids?: number[];
  etiqueta_ids_remover?: number[];
} {
  const corpo: { etiqueta_ids?: number[]; etiqueta_ids_remover?: number[] } = {};
  if (e.novas.length) corpo.etiqueta_ids = e.novas;
  if (e.remover.length) corpo.etiqueta_ids_remover = e.remover;
  return corpo;
}
